import { useState, useEffect } from 'react'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, UncontrolledTooltip } from 'reactstrap'
import s from './LinkOpportunityModal.scss'
import Select from 'react-select'
import { makePutRequest, makeGetRequest } from 'utils/api'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useLoading } from 'hooks'
import { LinkedDeals } from 'components/Deal/common/LinkedDeals'
import { PaginatedSelect } from 'components/common/PaginatedSelect'
import { toDealsSelect } from 'components/Deals/serializers'
import swal from 'sweetalert'
import { FaInfoCircle } from 'react-icons/fa'

const LinkOpportunityModal = ({ toggleLinkOpportunityModal, showLinkOpportunityModal }) => {
  const [linkedParent, setLinkedParent] = useState(null)
  const [linkedChildren, setLinkedChildren] = useState([])
  const [linkedRelated, setLinkedRelated] = useState([])
  const [selectedDeal, setSelectedDeal] = useState(null)
  const [selectedRelationship, setSelectedRelationship] = useState(null)
  const dispatch = useDispatch()
  const { id } = useParams()
  const [, withLoadingLocks] = useLoading()

  const getLinkedDeals = async () => {
    const response = await withLoadingLocks(makeGetRequest(`/v1/deals/linked/${id}`))
    setLinkedParent(response.parent)
    setLinkedChildren(response.children)
    setLinkedRelated(response.related)
  }

  useEffect(() => {
    ;(async () => {
      getLinkedDeals()
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isDealLinkable = async id => {
    if (
      ~[...linkedChildren, ...linkedRelated, ...(linkedParent ? [linkedParent] : [])].findIndex(
        deal => deal.id === id
      )
    ) {
      const shouldNavigate = await swal({
        title: `${selectedDeal.label} is already linked to the current one`,
        text: `If you want to change  the relationship please navigate to ${selectedDeal.label} page and unlink it first`,
        buttons: ['Cancel', 'Navigate'],
        icon: 'warning'
      })
      if (shouldNavigate) {
        window.open(`/opportunities/${selectedDeal.value}`, '_blank')
      }
      return false
    }
    const response = await withLoadingLocks(makeGetRequest(`/v1/deals/linked/${id}`))
    if (response.parent) {
      const shouldNavigate = await swal({
        title: `This opportunity is already linked to a parent - ${response.parent.name}`,
        text: `If you want to change the parent please navigate to ${response.parent.name} page and unlink it first`,
        buttons: ['Cancel', 'Navigate'],
        icon: 'warning'
      })
      if (shouldNavigate) {
        window.open(`/opportunities/${response.parent.id}`, '_blank')
      }
      return false
    }

    return true
  }

  const linkDeals = async () => {
    let dealId
    let parentId
    let isLinkable = true
    switch (selectedRelationship.value) {
      case 'parent':
        dealId = id
        parentId = selectedDeal.value
        break
      case 'child':
        dealId = selectedDeal.value
        parentId = id
        isLinkable = await isDealLinkable(dealId)
        break
      case 'related':
        dealId = selectedDeal.value
        parentId = linkedParent.id
        isLinkable = await isDealLinkable(dealId)
        break
    }
    try {
      if (!isLinkable) return

      await withLoadingLocks(makePutRequest(`/v1/deals/link/${dealId}/${parentId}`, {}))
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'Opportunities linked successfuly',
          level: 'success'
        }
      })
      getLinkedDeals()
      setSelectedDeal(null)
      setSelectedRelationship(null)
      dispatch({
        type: 'DEAL_UPDATE_SIDEBAR'
      })
    } catch (error) {
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'There was a problem linking the opportunities',
          level: 'error'
        }
      })
    }
  }

  const unlinkDeal = async (dealId, type) => {
    let dealIdToUnlink
    let parentId
    switch (type) {
      case 'parent':
        dealIdToUnlink = id
        parentId = dealId
        break
      case 'child':
        dealIdToUnlink = dealId
        parentId = id
        break
      case 'related':
        dealIdToUnlink = dealId
        parentId = linkedParent.id
        break
    }
    try {
      const confirm = await swal({
        title: 'Are you sure you want to unlink this opportunity?',
        buttons: ['No', 'Yes'],
        icon: 'warning'
      })
      if (confirm) {
        await withLoadingLocks(makePutRequest(`/v1/deals/unlink/${dealIdToUnlink}/${parentId}`, {}))
        dispatch({
          type: 'PUSH_NOTIFICATION',
          payload: {
            title: 'Opportunity unlinked successfuly',
            level: 'success'
          }
        })
        getLinkedDeals()
        dispatch({
          type: 'DEAL_UPDATE_SIDEBAR'
        })
      }
    } catch (error) {
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'There was a problem unlinking the opportunity',
          level: 'error'
        }
      })
    }
  }

  return (
    <>
      <Modal
        keyboard={false}
        backdrop="static"
        isOpen={showLinkOpportunityModal}
        toggle={toggleLinkOpportunityModal}
        size="lg"
      >
        <ModalHeader>Related Opportunities</ModalHeader>
        <ModalBody>
          <p className={s.title}>Currently Linked Opportunities</p>
          <LinkedDeals
            parent={linkedParent}
            kids={linkedChildren}
            related={linkedRelated}
            unlinkDeal={unlinkDeal}
          />
          <br />
          <p className={s.title}>Link other Opportunities to this one</p>
          <PaginatedSelect
            url="/v1/deals"
            serializer={toDealsSelect}
            className={s.input}
            onChange={setSelectedDeal}
            value={selectedDeal}
          />
          <Select
            className={s.relInput}
            value={selectedRelationship}
            options={[
              ...(!linkedParent
                ? [{ value: 'parent', label: 'Parent' }]
                : [{ value: 'related', label: 'Related' }]),
              { value: 'child', label: 'Child' }
            ]}
            onChange={setSelectedRelationship}
          />
          <FaInfoCircle id="Type1" style={{ position: 'relative', right: 10 }} />
          <UncontrolledTooltip placement="top" target="Type1">
            Select <b>Child</b> if you want the selected opportunity to become a <i>Child</i> of the
            existing opportunity. i.e. Metropolis Building is the <i>Parent</i> original opportunity
            and the selected opportunity Metropolis - 2nd Floor is the <i>Child</i> of Metropolis
            Building.
            <br />
            <br />
            Select <b>{linkedParent ? 'Related' : 'Parent'}</b> if you want the selected opportunity
            to become the <i>{linkedParent ? 'sister' : 'parent'}</i> of the existing opportunity.
            i.e. Metropolis - 2nd Floor is the original opportunity and the selected opportunity
            Metropolis - 3nd Floor is the <i>{linkedParent ? 'sister' : 'parent'}</i> of Metropolis
            - 2nd Floor.
          </UncontrolledTooltip>
          <Button
            className={s.saveBtn}
            onClick={linkDeals}
            disabled={!selectedDeal || !selectedRelationship}
          >
            Link
          </Button>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleLinkOpportunityModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default LinkOpportunityModal
